import React from "react";
import { Filter } from "lucide-react";
import { FiInfo } from "react-icons/fi";
import { Dropdown, Menu, Tooltip } from "antd";
import styled from "styled-components";
import { getStatusFilterOptions } from "./CardsView.utils";
import {
  TableHeader,
  EditableHeader,
  CheckboxHeader,
  FilterIconWrapper,
  CustomDropdownWrapper,
  ActionButtonHeader,
} from "./CardsView.styles";

const TitleHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
`;

const InfoIconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  cursor: help;

  .info-icon {
    color: #6b7280;
    font-size: 14px;
    transition: color 0.2s ease;

    &:hover {
      color: #3b82f6;
    }
  }
`;

const TableHeaders = ({
  columnOrder,
  columnVisibility,
  columnFilters,
  onBulkEditModalOpen,
  onStatusFilterChange,
  onOcrStatusFilterChange,
  isAllSelected,
  onSelectAll,
  cards,
  isEditModeEnabled = true,
  showTitleLengthFilter = false,
}) => {
  const statusFilterOptions = getStatusFilterOptions({ showTitleLengthFilter });
  // Helper function to get style for editable headers
  const getEditableHeaderStyle = () => ({
    cursor: isEditModeEnabled ? "pointer" : "not-allowed",
    opacity: isEditModeEnabled ? 1 : 0.6,
  });

  const renderTableHeaders = () => {
    return columnOrder.map((columnKey) => {
      if (!columnVisibility[columnKey]) return null;

      switch (columnKey) {
        case "images":
          return <TableHeader key={columnKey}>Images</TableHeader>;
        case "status":
          return (
            <EditableHeader key={columnKey}>
              <div
                onClick={() =>
                  isEditModeEnabled && onBulkEditModalOpen("status")
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: isEditModeEnabled ? "pointer" : "not-allowed",
                  opacity: isEditModeEnabled ? 1 : 0.6,
                }}
              >
                Status
                <FilterIconWrapper onClick={(e) => e.stopPropagation()}>
                  <CustomDropdownWrapper>
                    <Dropdown
                      overlay={
                        <Menu
                          selectedKeys={
                            columnFilters.status ? [columnFilters.status] : []
                          }
                          onClick={({ key }) =>
                            onStatusFilterChange(key === "all" ? "" : key)
                          }
                          items={statusFilterOptions.map((option) => ({
                            key: option.value || "all",
                            label: option.label,
                          }))}
                        />
                      }
                      trigger={["click"]}
                      placement="bottomLeft"
                      getPopupContainer={(trigger) => {
                        let container =
                          trigger.closest(".ant-table-container") ||
                          trigger.closest("[data-table-container]") ||
                          trigger.closest("section");
                        return container || document.body;
                      }}
                      destroyPopupOnHide={true}
                    >
                      <Filter size={12} />
                    </Dropdown>
                  </CustomDropdownWrapper>
                </FilterIconWrapper>
              </div>
            </EditableHeader>
          );
        case "sale_price":
          return (
            <EditableHeader
              key={columnKey}
              onClick={() =>
                isEditModeEnabled && onBulkEditModalOpen("sale_price")
              }
              style={{
                cursor: isEditModeEnabled ? "pointer" : "not-allowed",
                opacity: isEditModeEnabled ? 1 : 0.6,
              }}
            >
              Sale price
            </EditableHeader>
          );
        case "ebay_title":
          return (
            <TableHeader key={columnKey}>
              <TitleHeaderWrapper>
                Title
                <Tooltip
                  title="If input is empty for title, then title will be given by default based on the default title builder configuration"
                  placement="bottomRight"
                  getPopupContainer={(trigger) => {
                    let container =
                      trigger.closest(".ant-table-container") ||
                      trigger.closest("[data-table-container]") ||
                      trigger.closest("section") ||
                      trigger.closest("thead");
                    return container || document.body;
                  }}
                >
                  <InfoIconWrapper>
                    <FiInfo className="info-icon" />
                  </InfoIconWrapper>
                </Tooltip>
              </TitleHeaderWrapper>
            </TableHeader>
          );
        case "ebay_category":
          return (
            <EditableHeader
              key={columnKey}
              onClick={() =>
                isEditModeEnabled && onBulkEditModalOpen("ebay_category")
              }
            >
              Category
            </EditableHeader>
          );
        case "ebay_condition":
          return <TableHeader key={columnKey}>Condition</TableHeader>;
        case "ebay_description":
          return <TableHeader key={columnKey}>Description</TableHeader>;
        case "ebay_card_name":
          return <TableHeader key={columnKey}>Card name</TableHeader>;
        case "available_quantity":
          return (
            <EditableHeader
              key={columnKey}
              onClick={() =>
                isEditModeEnabled && onBulkEditModalOpen("available_quantity")
              }
              style={getEditableHeaderStyle()}
            >
              Available quantity
            </EditableHeader>
          );
        case "ebay_set":
          return (
            <EditableHeader
              key={columnKey}
              onClick={() =>
                isEditModeEnabled && onBulkEditModalOpen("ebay_set")
              }
            >
              Set
            </EditableHeader>
          );
        case "ebay_country":
          return (
            <EditableHeader
              key={columnKey}
              onClick={() =>
                isEditModeEnabled && onBulkEditModalOpen("ebay_country")
              }
            >
              Country
            </EditableHeader>
          );
        case "ebay_language":
          return (
            <EditableHeader
              key={columnKey}
              onClick={() =>
                isEditModeEnabled && onBulkEditModalOpen("ebay_language")
              }
            >
              Language
            </EditableHeader>
          );
        case "ebay_manufacturer":
          return (
            <EditableHeader
              key={columnKey}
              onClick={() =>
                isEditModeEnabled && onBulkEditModalOpen("ebay_manufacturer")
              }
            >
              Manufacturer
            </EditableHeader>
          );
        case "ebay_autographed":
          return (
            <EditableHeader
              key={columnKey}
              onClick={() =>
                isEditModeEnabled && onBulkEditModalOpen("ebay_autographed")
              }
            >
              Autographed
            </EditableHeader>
          );
        case "ebay_card_number":
          return <TableHeader key={columnKey}>Card number</TableHeader>;
        case "ebay_year_manufactured":
          return (
            <EditableHeader
              key={columnKey}
              onClick={() =>
                isEditModeEnabled &&
                onBulkEditModalOpen("ebay_year_manufactured")
              }
            >
              Year manufactured
            </EditableHeader>
          );
        case "ebay_graded":
          return (
            <EditableHeader
              key={columnKey}
              onClick={() =>
                isEditModeEnabled && onBulkEditModalOpen("ebay_graded")
              }
            >
              Graded
            </EditableHeader>
          );
        case "ebay_vintage":
          return (
            <EditableHeader
              key={columnKey}
              onClick={() =>
                isEditModeEnabled && onBulkEditModalOpen("ebay_vintage")
              }
            >
              Vintage
            </EditableHeader>
          );
        case "ebay_condition_description":
          return (
            <EditableHeader
              key={columnKey}
              onClick={() =>
                isEditModeEnabled &&
                onBulkEditModalOpen("ebay_condition_description")
              }
            >
              Condition description
            </EditableHeader>
          );
        default: {
          const label = columnKey.replace(/_/g, " ");
          return (
            <TableHeader key={columnKey}>
              {label.charAt(0).toUpperCase() + label.slice(1)}
            </TableHeader>
          );
        }
      }
    });
  };

  const handleSelectAll = () => {
    if (onSelectAll) {
      onSelectAll(!isAllSelected);
    }
  };

  return (
    <>
      <CheckboxHeader>
        <input
          type="checkbox"
          checked={isAllSelected || false}
          onChange={handleSelectAll}
        />
      </CheckboxHeader>
      <ActionButtonHeader />
      {renderTableHeaders()}
    </>
  );
};

export default TableHeaders;
